import { Suspense, useEffect } from "react";
import { useLocation, useRoutes } from "react-router-dom";
import { ThemeProvider } from "@/components/theme-provider";
import { Toaster } from "sonner";
import MaintenancePage from "@/components/MaintenancePage";
import { routes, processRoutes } from "./routes/routes";
import { CartProvider } from "./context/CartContext";
import { ProductStateProvider } from "./context/ProductStateContext";
import { TooltipProvider } from "@radix-ui/react-tooltip";
import { AuthProvider } from "./context/AuthContext";
import ScrollToTop from "./components/ScrollToTop";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { GlobalErrorHandler } from "./components/wizard/shared/GlobalErrorHandler";
import { useReferralTracking } from "./hooks/useReferralTracking";
import { SentryErrorBoundary } from "@/lib/sentry";
import { Button } from "@/components/ui/button";

const queryClient = new QueryClient();

const AppRoutes = () => {
  return useRoutes(processRoutes(routes));
};

function App() {
  const location = useLocation();

  // Track affiliate referrals from URL parameters
  useReferralTracking();

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Check for maintenance mode
  const isMaintenanceMode = import.meta.env.VITE_MAINTENANCE_MODE === "true";
  const maintenanceMessage = (import.meta.env.VITE_MAINTENANCE_MESSAGE as string) || undefined;

  // Show maintenance page if enabled
  if (isMaintenanceMode) {
    return (
      <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
        <CartProvider>
          <MaintenancePage message={maintenanceMessage} />
          <Toaster />
        </CartProvider>
      </ThemeProvider>
    );
  }

  return (
    <SentryErrorBoundary
      fallback={({ error: _error, resetError }) => (
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="text-center space-y-4 max-w-md p-6">
            <h2 className="text-2xl font-bold">Oops! Something went wrong</h2>
            <p className="text-muted-foreground">
              We&apos;ve been notified and are working on a fix. Please try refreshing the page.
            </p>
            <Button onClick={resetError}>Try Again</Button>
          </div>
        </div>
      )}
    >
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <AuthProvider>
            <ProductStateProvider>
              <CartProvider>
                <GlobalErrorHandler />
                <Toaster position="top-right" closeButton={true} />
                <ScrollToTop />
                <Suspense
                  fallback={
                    <div className="h-screen flex items-center justify-center bg-background">
                      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
                    </div>
                  }
                >
                  <AppRoutes />
                </Suspense>
              </CartProvider>
            </ProductStateProvider>
          </AuthProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </SentryErrorBoundary>
  );
}

export default App;
